import React, { useState, useEffect } from "react";
import {
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaSearch,
  FaFilter,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const InstituteManagement = () => {
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    fetchInstitutes();
  }, []);

  const fetchInstitutes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/institute/admin/all`,
        {
          withCredentials: true,
        }
      );
      setInstitutes(response.data.institutes);
    } catch (error) {
      console.error("Error fetching institutes:", error);
      setError("Failed to fetch institutes");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (instituteId, currentStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BASE_URL}/api/institute/admin/${instituteId}/status`,
        { is_enabled: !currentStatus },
        {
          withCredentials: true,
        }
      );

      // Update local state
      setInstitutes(
        institutes.map((institute) =>
          institute.id === instituteId
            ? { ...institute, is_enabled: !currentStatus }
            : institute
        )
      );
    } catch (error) {
      console.error("Error updating institute status:", error);
      setError("Failed to update institute status");
    }
  };

  const handleViewDetails = (instituteId) => {
    navigate(`/institute-admin/details/${instituteId}`);
  };

  // Filter institutes based on search term, status, and type
  const filteredInstitutes = institutes.filter((institute) => {
    const matchesSearch =
      institute.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      institute.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      institute.contact_person.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "enabled" && institute.is_enabled) ||
      (filterStatus === "disabled" && !institute.is_enabled);

    const matchesType =
      filterType === "all" || institute.institute_type === filterType;

    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Loading institutes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Institute Management
          </h1>
          <button
            onClick={() => navigate("/DsportAdmin")}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 w-full sm:w-auto justify-center inline-flex"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search institutes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="relative">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="school">School</option>
                <option value="college">College</option>
                <option value="university">University</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Institutes Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Institute
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInstitutes.map((institute) => (
                  <tr key={institute.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {institute.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {institute.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                        {institute.institute_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        {institute.city}, {institute.country}
                      </div>
                      <div className="text-gray-500">{institute.area}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{institute.contact_person}</div>
                      <div className="text-gray-500">
                        {Array.isArray(institute.telephone)
                          ? institute.telephone.join(", ")
                          : institute.telephone || "Not provided"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="text-green-600">
                        Active: {institute.active_users_count}
                      </div>
                      <div className="text-red-600">
                        Left: {institute.left_users_count}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          institute.is_enabled
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {institute.is_enabled ? "Enabled" : "Disabled"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(institute.id)}
                          className="text-blue-600 hover:text-blue-900 p-2"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() =>
                            handleToggleStatus(
                              institute.id,
                              institute.is_enabled
                            )
                          }
                          className={`p-2 ${
                            institute.is_enabled
                              ? "text-red-600 hover:text-red-900"
                              : "text-green-600 hover:text-green-900"
                          }`}
                          title={institute.is_enabled ? "Disable" : "Enable"}
                        >
                          {institute.is_enabled ? (
                            <FaToggleOff />
                          ) : (
                            <FaToggleOn />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredInstitutes.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg">No institutes found</div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {institutes.length}
              </div>
              <div className="text-gray-500">Total Institutes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {institutes.filter((i) => i.is_enabled).length}
              </div>
              <div className="text-gray-500">Enabled</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {institutes.filter((i) => !i.is_enabled).length}
              </div>
              <div className="text-gray-500">Disabled</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {institutes.reduce(
                  (sum, i) => sum + parseInt(i.active_users_count),
                  0
                )}
              </div>
              <div className="text-gray-500">Total Active Users</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstituteManagement;
